import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  StyledTextSub,
  StyledText,
} from '../../../components/StyledTypography';
import { useTranslate } from '@im/react-localization';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';
import { V2RecordResponse } from '../hooks/advanced-search';

export default function SearchResultRow(
  params: Readonly<{ deal: V2RecordResponse }>
) {
  const deal = params.deal;
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');
  const companyCode = searchParams.get('companyCode');
  const translate = useTranslate();
  const formatDateTime = useDateTimeFormatter();
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={5}
      key={deal.deal_number}
      mt={2}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: `${theme.shape.borderRadius}px`,
        padding: theme.spacing(1.5),
      })}
    >
      <Stack
        direction="row"
        divider={<Divider flexItem orientation="vertical" />}
        flexWrap="inherit"
        gap={1.5}
        justifyContent="space-between"
        width="100%"
      >
        <Stack>
          <StyledText noWrap width="152px">
            <Link
              data-testid="gds-vp-searchResultRow-deal"
              style={{
                textDecoration: 'none',
              }}
              to={`/vendor-program/details-page?${
                deal.business_type === 'Subscription'
                  ? `businessType=${deal.business_type}&`
                  : ''
              }dealNumber=${encodeURIComponent(
                deal.deal_number
              )}&importreferenceid=${deal.importreferenceid}&dealid=${
                deal.id
              }&vendorProgramId=${deal.vendor_program_id}&customerset_id=${
                deal.customerseT_ID
              }&countryCode=${countryCode}&companyCode=${companyCode}`}
            >
              <Typography
                sx={(theme) => ({
                  color: theme.palette.primary[700],
                  fontSize: theme.typography.body1.fontSize,
                  lineHeight: theme.typography.pxToRem(22),
                })}
              >
                <Tooltip title={deal.deal_number}>
                  <span>{deal.deal_number}</span>
                </Tooltip>
              </Typography>
            </Link>
          </StyledText>
          <Stack direction="row" spacing={1}>
            <StyledTextSub>
              {translate('VendorDeals.DealNoShort')}
            </StyledTextSub>
            <Chip
              label={`v${deal.version_number}`}
              sx={(theme) => ({
                background: theme.palette.grey[100],
                borderRadius: `calc(${theme.shape.borderRadius}px - 2px)`,
                fontSize: theme.typography.body2.fontSize,
                height: '16px',
                padding: `0 ${theme.spacing(0.5)}`,
              })}
            />
          </Stack>
        </Stack>
        <Stack>
          <StyledText
            data-testid="gds-vp-searchResultRow-vendorName"
            noWrap
            width="144px"
          >
            <Tooltip title={deal.vendor_name}>
              <span>{deal.vendor_name}</span>
            </Tooltip>
          </StyledText>
          <StyledTextSub>{translate('VendorDeals.Vendor')}</StyledTextSub>
        </Stack>
        <Stack>
          <StyledText noWrap width="120px">
            <Tooltip title={deal.vendor_program_name}>
              <span>{deal?.vendor_program_name}</span>
            </Tooltip>
          </StyledText>
          <StyledTextSub>
            {translate('VendorProgram.VendorProgram')}
          </StyledTextSub>
        </Stack>
        <Stack>
          <StyledText noWrap width="140px">
            <Tooltip title={deal.reseller_name}>
              <span>{deal.reseller_name}</span>
            </Tooltip>
          </StyledText>
          <StyledTextSub>{translate('VendorProgram.Reseller')}</StyledTextSub>
        </Stack>
        {/* Business Type before End User */}
        <Stack>
          <StyledText noWrap width="120px">
            <Tooltip title={deal.business_type}>
              <span>{deal.business_type}</span>
            </Tooltip>
          </StyledText>
          <StyledTextSub>
            {translate('VendorProgram.BusinessType')}
          </StyledTextSub>
        </Stack>
        <Stack>
          <StyledText noWrap width="140px">
            <Tooltip title={deal.enduser_name}>
              <span>{deal.enduser_name}</span>
            </Tooltip>
          </StyledText>
          <StyledTextSub>{translate('VendorProgram.EndUser')}</StyledTextSub>
        </Stack>
        <Stack>
          <StyledText>
            <Tooltip title={formatDateTime(deal.deal_ingested_on, true)}>
              <span>{formatDateTime(deal.deal_ingested_on, true)}</span>
            </Tooltip>
          </StyledText>
          <StyledTextSub noWrap width="128px">
            {translate('VendorProgram.UploadDate')}
          </StyledTextSub>
        </Stack>
        <Stack>
          <StyledText>
            <Tooltip title={formatDateTime(deal.deal_start)}>
              <span>{formatDateTime(deal.deal_start)}</span>
            </Tooltip>
          </StyledText>
          <StyledTextSub noWrap width="128px">
            {translate('VendorProgram.EffectiveDate')}
          </StyledTextSub>
        </Stack>
        <Stack>
          <StyledText>
            <Tooltip title={formatDateTime(deal.deal_expiry)}>
              <span>{formatDateTime(deal.deal_expiry)}</span>
            </Tooltip>
          </StyledText>
          <StyledTextSub noWrap width="128px">
            {translate('VendorProgram.ExpirationDate')}
          </StyledTextSub>
        </Stack>
        <Stack>
          <StyledText>
            <Tooltip title={formatDateTime(deal.price_protection_expiry)}>
              <span>{formatDateTime(deal.price_protection_expiry)}</span>
            </Tooltip>
          </StyledText>
          <StyledTextSub noWrap width="128px">
            {translate('VendorProgram.PriceProtectionDate')}
          </StyledTextSub>
        </Stack>
      </Stack>
    </Stack>
  );
}
